import React, { useState } from 'react';
import { searchNearbyPlaces } from '@/services/placesAPI';
import { batchSaveGooglePlaces } from '@/services/enhancedCampDatabaseService';
import { checkCampAuditDataSchema, testBasicInsert } from '@/utils/debugDatabaseSchema';

const QuickIntegrationTest = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const runTest = async () => {
    setLoading(true);
    setError(null);
    setResults(null);

    console.group('🧪 Quick Integration Test');
    console.log('Starting test at:', new Date().toISOString());

    try {
      // Step 0: Check database schema
      console.log('📋 Step 0: Checking database schema...');
      const schemaCheck = await checkCampAuditDataSchema();
      
      if (!schemaCheck.exists) {
        throw new Error('camp_audit_data table does not exist or is not accessible. Please run database_schema_caching.sql first.');
      }
      
      console.log('✅ Schema check passed');
      
      // Step 0.5: Test basic insert
      console.log('🧪 Step 0.5: Testing basic insert...');
      const insertTest = await testBasicInsert();
      
      if (!insertTest.success) {
        throw new Error(`Basic insert test failed: ${insertTest.error?.message || 'Unknown error'}. Check console for details.`);
      }
      
      console.log('✅ Basic insert test passed');
      
      // Step 1: Search for places
      console.log('📍 Step 1: Searching for nearby places...');
      console.log('Location: Atlanta, GA (33.7490, -84.3880)');
      console.log('Radius: 5 miles');
      
      const searchResults = await searchNearbyPlaces({ lat: 33.7490, lng: -84.3880 }, 5);
      
      console.log(`✅ Search complete: Found ${searchResults.length} results`);
      console.log('First 3 results:', searchResults.slice(0, 3).map(r => ({
        place_id: r.place_id,
        name: r.name,
        location: r.location
      })));

      if (searchResults.length === 0) {
        throw new Error('No results returned from Google Places API');
      }

      // Step 2: Save to database
      console.log('💾 Step 2: Saving first 3 results to database...');
      const testResults = searchResults.slice(0, 3);
      
      const saveResult = await batchSaveGooglePlaces(testResults);
      
      console.log('✅ Save complete:', saveResult.summary);
      console.log('Successful saves:', saveResult.successful);
      console.log('Errors:', saveResult.errors);

      // Set results for UI display
      setResults({
        searchCount: searchResults.length,
        savedCount: saveResult.summary.successful,
        failedCount: saveResult.summary.failed,
        searchResults: testResults,
        saveResult: saveResult,
        success: saveResult.summary.successful > 0
      });

      console.groupEnd();
      console.log('🎉 Test completed successfully!');

    } catch (err) {
      console.error('❌ Test failed:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack
      });
      console.groupEnd();
      
      setError({
        message: err.message,
        details: err.toString()
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            🧪 Quick Integration Test
          </h1>
          
          <p className="text-gray-600 mb-6">
            This test will search for camps near Atlanta, GA and attempt to save the first 3 results to the database.
          </p>

          <button
            onClick={runTest}
            disabled={loading}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium mb-6"
          >
            {loading ? '🔄 Running Test...' : '▶️ Run Test'}
          </button>

          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-300 rounded-lg">
              <h3 className="font-semibold text-red-800 mb-2">❌ Test Failed</h3>
              <p className="text-red-700 mb-2">{error.message}</p>
              <pre className="text-xs text-red-600 overflow-auto">
                {error.details}
              </pre>
            </div>
          )}

          {results && (
            <div className="space-y-4">
              {results.success ? (
                <div className="p-4 bg-green-100 border border-green-300 rounded-lg">
                  <h3 className="font-semibold text-green-800 mb-2">✅ Test Successful!</h3>
                  <p className="text-green-700">
                    Successfully saved {results.savedCount} out of {results.searchCount} places to database
                  </p>
                </div>
              ) : (
                <div className="p-4 bg-yellow-100 border border-yellow-300 rounded-lg">
                  <h3 className="font-semibold text-yellow-800 mb-2">⚠️ Partial Success</h3>
                  <p className="text-yellow-700">
                    Saved {results.savedCount} records, {results.failedCount} failed
                  </p>
                </div>
              )}

              <div className="p-4 bg-blue-50 border border-blue-300 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">📊 Test Summary</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>🔍 Places found: {results.searchCount}</li>
                  <li>✅ Successfully saved: {results.savedCount}</li>
                  <li>❌ Failed: {results.failedCount}</li>
                </ul>
              </div>

              {results.searchResults && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3">🏕️ Places Tested</h4>
                  <div className="space-y-3">
                    {results.searchResults.map((place, index) => (
                      <div key={place.place_id} className="bg-white p-3 rounded border border-gray-200">
                        <div className="flex justify-between items-start">
                          <div>
                            <h5 className="font-medium text-gray-900">{place.name}</h5>
                            <p className="text-sm text-gray-600">{place.formatted_address}</p>
                            <div className="flex items-center gap-4 mt-2">
                              {place.rating && (
                                <span className="text-xs text-gray-500">
                                  ⭐ {place.rating} ({place.user_ratings_total} reviews)
                                </span>
                              )}
                              {place.location && (
                                <span className="text-xs text-gray-500">
                                  📍 {place.location.lat.toFixed(4)}, {place.location.lng.toFixed(4)}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            results.saveResult.successful.find(s => s?.place_id === place.place_id)
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {results.saveResult.successful.find(s => s?.place_id === place.place_id)
                              ? '✅ Saved'
                              : '❌ Failed'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {results.saveResult.errors.length > 0 && (
                <div className="p-4 bg-red-50 border border-red-300 rounded-lg">
                  <h4 className="font-semibold text-red-900 mb-2">❌ Errors Encountered</h4>
                  <ul className="text-sm text-red-700 space-y-2">
                    {results.saveResult.errors.map((err, index) => (
                      <li key={index} className="border-l-2 border-red-400 pl-2">
                        <strong>{err.place?.name || 'Unknown place'}</strong>: {err.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="mt-6 p-4 bg-gray-100 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">📝 Instructions</h4>
            <ol className="text-sm text-gray-700 space-y-1 list-decimal list-inside">
              <li>Click "Run Test" to execute the integration test</li>
              <li>Check the browser console for detailed logs</li>
              <li>Review the results displayed above</li>
              <li>If errors occur, check the error details in the console</li>
            </ol>
          </div>

          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">🔍 Console Commands</h4>
            <p className="text-sm text-blue-800 mb-2">You can also run these commands in the browser console:</p>
            <pre className="text-xs bg-white p-2 rounded border border-blue-200 overflow-auto">
{`import { searchNearbyPlaces } from '@/services/placesAPI';
import { batchSaveGooglePlaces } from '@/services/enhancedCampDatabaseService';

const results = await searchNearbyPlaces({ lat: 33.7490, lng: -84.3880 }, 5);
const saveResult = await batchSaveGooglePlaces(results.slice(0, 3));`}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickIntegrationTest;
